import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ScenarioMetaForm from '../../components/Budget/ScenarioMetaForm';
import {
    EMPTY_SCENARIO_META,
    ScenarioMetaValue,
    hasScenarioMetaErrors,
    toScenarioPayload,
} from '../../components/Budget/scenarioMeta';
import { BudgetRoutes, budgetScenarioDetailRoute } from '../../enums/BudgetRoutes';
import { useCreateBudgetScenario } from '../../hooks/api/budgetScenarios';
import { useBudgetContext } from '../../store/Budget/BudgetContext';
import { useTitleContext } from '../../store/Page/TitleContext';

// The board's "Neues Szenario anlegen" screen. Metadata only: the type-specific monthly inputs need a
// scenario id to save against (every input grid autosaves per row), so saving here lands directly on
// the scenario's own page where they are entered.
const NewBudgetScenario = () => {
    const { t } = useTranslation('budget');
    const navigate = useNavigate();
    const { onChange: changeTitle } = useTitleContext();
    const { versions, canEdit } = useBudgetContext();
    const [value, setValue] = useState<ScenarioMetaValue>(EMPTY_SCENARIO_META);

    const { mutateAsync: createScenario, isLoading: isCreating } = useCreateBudgetScenario();

    useEffect(() => {
        changeTitle(t('scenarios.add'));
        return () => {
            changeTitle(undefined);
        };
    }, [changeTitle, t]);

    const hasErrors = useMemo(() => hasScenarioMetaErrors(value, t), [value, t]);

    const handleSubmit = useCallback(async () => {
        if (hasErrors) return;
        try {
            const created = await createScenario(toScenarioPayload(value));
            navigate(budgetScenarioDetailRoute(created.id));
        } catch {
            // The mutation's onError already raised the toast; keep the form filled for a retry.
        }
    }, [hasErrors, createScenario, value, navigate]);

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconButton
                    aria-label={t('scenarioDetail.backToOverview')}
                    title={t('scenarioDetail.backToOverview')}
                    onClick={() => navigate(BudgetRoutes.SCENARIOS)}
                >
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6" fontWeight="bold">
                    <span>{t('scenarios.add')}</span>
                </Typography>
            </Stack>

            {!canEdit ? (
                <Alert severity="info">
                    <span>{t('scenarios.readOnlyRole')}</span>
                </Alert>
            ) : versions.length === 0 ? (
                <Alert severity="info">
                    <span>{t('scenarios.noVersions')}</span>
                </Alert>
            ) : (
                <>
                    <ScenarioMetaForm value={value} onChange={setValue} mode="create" disabled={isCreating} />

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        <span>{t('scenarios.inputsAfterSave')}</span>
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" disabled={hasErrors || isCreating} onClick={handleSubmit}>
                            <span>{isCreating ? t('action.saving') : t('action.save')}</span>
                        </Button>
                        <Button
                            variant="outlined"
                            disabled={isCreating}
                            onClick={() => navigate(BudgetRoutes.SCENARIOS)}
                        >
                            <span>{t('action.cancel')}</span>
                        </Button>
                    </Stack>
                </>
            )}
        </Box>
    );
};

export default NewBudgetScenario;
